/**
 * Document Generation API
 * Generic document generation endpoints
 */

import { apiClient } from '@shared/api/client';
import { useSessionStore } from '@/shared/stores/sessionStore';
import type {
  TemplateListResponse,
  UploadTemplateResponse,
  ExtractVariablesResponse,
  GenerateDocumentsRequest,
  GenerateDocumentsResponse,
  DocGenState,
  DraftSummary,
  PlaceholderAnalysis,
  PlaceholderBatchAnalysisResponse,
  EntityItem,
  OfficerItem,
  OfficerDetail,
} from '../types/docGen.types';

export const docGenApi = {
  /**
   * 템플릿 목록 조회 (카테고리별)
   */
  getTemplates: (params?: {
    doc_type?: string;
    search?: string;
    limit?: number;
    offset?: number;
  }): Promise<TemplateListResponse> => {
    return apiClient.get('/aidocuments/templates', { params }) as any;
  },

  /**
   * 템플릿 업로드 + 변수 추출
   */
  uploadTemplate: (file: File, docType: string): Promise<UploadTemplateResponse> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('doc_type', docType);
    return apiClient.post('/aidocuments/upload-template', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }) as any;
  },

  /**
   * 기존 템플릿에서 변수 추출
   */
  extractVariables: (params: {
    templateId?: number;
    uploadId?: string;
  }): Promise<ExtractVariablesResponse> => {
    return apiClient.post('/aidocuments/extract-variables', params) as any;
  },

  /**
   * 문서 생성 (entity별 batch)
   */
  generateDocuments: (request: GenerateDocumentsRequest): Promise<GenerateDocumentsResponse> => {
    return apiClient.post('/aidocuments/generate-documents', request) as any;
  },

  /**
   * 드래프트 목록 조회
   */
  getDrafts: (): Promise<{ success: boolean; drafts: DraftSummary[] }> => {
    return apiClient.get('/aidocuments/docgen/drafts') as any;
  },

  /**
   * 상태 저장 (Redis, draftId 포함)
   */
  saveState: (state: DocGenState & { draftId: string }): Promise<{ success: boolean; saved_at?: string; draftId?: string }> => {
    return apiClient.post('/aidocuments/docgen/save-state', state) as any;
  },

  /**
   * 특정 드래프트 로드
   */
  loadState: (draftId?: string): Promise<{ success: boolean; state?: DocGenState }> => {
    if (draftId) {
      return apiClient.get(`/aidocuments/docgen/state/${draftId}`) as any;
    }
    return apiClient.get('/aidocuments/docgen/state') as any;
  },

  /**
   * 특정 드래프트 삭제
   */
  deleteState: (draftId?: string): Promise<{ success: boolean }> => {
    if (draftId) {
      return apiClient.delete(`/aidocuments/docgen/state/${draftId}`) as any;
    }
    return apiClient.delete('/aidocuments/docgen/state') as any;
  },

  // ── Placeholder Analysis ────────────────────────────────────────

  /**
   * 단일 플레이스홀더 AI 분석
   */
  analyzePlaceholder: (params: {
    placeholder: string;
    docType?: string;
    allPlaceholders?: string[];
    userHint?: string;
  }): Promise<PlaceholderAnalysis> => {
    return apiClient.post('/aidocuments/analyze-placeholder', params) as any;
  },

  /**
   * 여러 플레이스홀더 일괄 AI 분석
   */
  analyzePlaceholdersBatch: (params: {
    placeholders: string[];
    docType?: string;
    uploadId?: string;
    templateId?: number;
  }): Promise<PlaceholderBatchAnalysisResponse> => {
    return apiClient.post('/aidocuments/analyze-placeholders-batch', params) as any;
  },

  /**
   * DOCX 섹션 요약 (GPT)
   */
  summarizeSections: (params: {
    sections: string[];
  }): Promise<{ success: boolean; summaries: Record<string, string>; sources: Record<string, string> }> => {
    return apiClient.post('/aidocuments/summarize-sections', params) as any;
  },

  // ── Entity / Officer Data ───────────────────────────────────────

  /**
   * Entity 목록 조회
   */
  getEntities: (): Promise<{ success: boolean; data: any }> => {
    const sessionStore = useSessionStore();
    const jurisdiction = String(sessionStore.getCustomerInfo()?.jurisdiction || '').toUpperCase();
    const isMY = jurisdiction === 'MY' || jurisdiction === 'MALAYSIA';

    // ✅ Malaysia 계정은 entity_my 로 호출 (권한/데이터 규칙이 MY 전용으로 확장된 케이스)
    if (isMY) {
      // MY는 전체 엔티티 수가 많아 limit(100)만으로는 검색에서 누락될 수 있어 all=true로 로드 후 클라이언트 필터 사용
      return apiClient.get('/entity_my', { params: { all: true, onlyActive: true, onlyLive: true } }) as any;
    }

    // NOTE:
    // - /api/entity 는 메뉴 권한(/entity view) 필요 → DocGen(Task) 컨텍스트에서 AUTH002가 날 수 있음
    // - 공통 엔티티 선택은 startTask/common/entities 로 우회 (Tasks 메뉴 권한 컨텍스트)
    return apiClient.get('/startTask/common/entities') as any;
  },

  /**
   * Entity 상세 조회
   */
  getEntityDetail: (entityCd: string): Promise<{ success: boolean; data: EntityItem }> => {
    return apiClient.get(`/entity/${entityCd}`) as any;
  },

  /**
   * Entity Profile 조회 (주소, 회계날짜, 활동 포함)
   * MY 관할권이면 /entity_my/profile, 그 외에는 /entity/profile
   */
  getEntityProfile: (entityCd: string, jurisdiction?: string): Promise<{ success: boolean; data: any }> => {
    const isMY = jurisdiction?.toUpperCase() === 'MY';
    const base = isMY ? '/entity_my/profile' : '/entity/profile';
    return apiClient.get(`${base}/${entityCd}`) as any;
  },

  /**
   * Director 목록 — MY: /documents/variables-my, SG: /documents/variables
   */
  getDirectors: (entityCd: string): Promise<{ success: boolean; data: { directors: OfficerItem[] } }> => {
    const sessionStore = useSessionStore();
    const jurisdiction = String(sessionStore.getCustomerInfo()?.jurisdiction || '').toUpperCase();
    const isMY = jurisdiction === 'MY' || jurisdiction === 'MALAYSIA';
    const base = isMY ? '/documents/variables-my' : '/documents/variables';
    return apiClient.get(`${base}/directors/${entityCd}`) as any;
  },

  /**
   * Secretary 목록 — MY: /documents/variables-my, SG: /documents/variables
   */
  getSecretaries: (entityCd: string): Promise<{ success: boolean; data: { secretaries: OfficerItem[] } }> => {
    const sessionStore = useSessionStore();
    const jurisdiction = String(sessionStore.getCustomerInfo()?.jurisdiction || '').toUpperCase();
    const isMY = jurisdiction === 'MY' || jurisdiction === 'MALAYSIA';
    const base = isMY ? '/documents/variables-my' : '/documents/variables';
    return apiClient.get(`${base}/secretaries/${entityCd}`) as any;
  },

  /**
   * CEO 목록
   */
  getCeos: (entityCd: string): Promise<{ success: boolean; data: { ceos: OfficerItem[] } }> => {
    return apiClient.get(`/documents/variables/ceos/${entityCd}`) as any;
  },

  /**
   * Auditor 목록
   */
  getAuditors: (entityCd: string): Promise<{ success: boolean; data: { auditors: OfficerItem[] } }> => {
    return apiClient.get(`/documents/variables/auditors/${entityCd}`) as any;
  },

  /**
   * Manager 목록 (POSITION_NAME 에 'Manager' 포함된 L_OFFICER row)
   */
  getManagers: (entityCd: string): Promise<{ success: boolean; data: { managers: OfficerItem[] } }> => {
    return apiClient.get(`/documents/variables/managers/${entityCd}`) as any;
  },

  /**
   * Controller 목록
   */
  getControllers: (entityCd: string): Promise<{ success: boolean; data: any[] }> => {
    const sessionStore = useSessionStore();
    const jurisdiction = String(sessionStore.getCustomerInfo()?.jurisdiction || '').toUpperCase();
    const isMY = jurisdiction === 'MY' || jurisdiction === 'MALAYSIA';
    const base = isMY ? '/entity_my/controller' : '/entity/controller';
    return apiClient.get(`${base}/${entityCd}`) as any;
  },

  /**
   * Beneficial Owner 목록.
   * - MY: `/entity_my/beneficial-owner/:id` — L_BENEFICIALOWNER 테이블 (Controller 와 분리)
   * - SG: `/entity/controller/:id` — L_CONTROLLER 와 동일 소스 (SG 는 BO 별도 테이블 없음)
   */
  getBeneficialOwners: (entityCd: string): Promise<{ success: boolean; data: any }> => {
    const sessionStore = useSessionStore();
    const jurisdiction = String(sessionStore.getCustomerInfo()?.jurisdiction || '').toUpperCase();
    const isMY = jurisdiction === 'MY' || jurisdiction === 'MALAYSIA';
    if (isMY) {
      // _ts: 304 캐시로 ownership/criteria 빠지는 것 방지
      return apiClient.get(`/entity_my/beneficial-owner/${entityCd}`, {
        params: { _ts: Date.now() },
      }) as any;
    }
    return apiClient.get(`/entity/controller/${entityCd}`) as any;
  },

  /**
   * Shareholder 목록
   */
  getShareholders: (entityCd: string): Promise<{ success: boolean; data: { shares: any[] } }> => {
    return apiClient.get(`/documents/variables/shares/${entityCd}`) as any;
  },

  /**
   * Members 목록 — jurisdiction 별 분기.
   *
   * MY 는 `getShareholders` (variables/shares) 가 SG-style SHARE_CAPITAL_IDX join 으로 인해
   * 일부 member 를 누락 → 전용 endpoint `/entity_my/members/:entityCd` 사용.
   * SG 는 기존 shares endpoint 재사용 (응답 key 만 `shares` → `members` 로 정규화).
   */
  getMembers: async (
    entityCd: string
  ): Promise<{ success: boolean; data: { members: any[] } }> => {
    const sessionStore = useSessionStore();
    const jurisdiction = String(sessionStore.getCustomerInfo()?.jurisdiction || '').toUpperCase();
    const isMY = jurisdiction === 'MY' || jurisdiction === 'MALAYSIA';
    if (isMY) {
      return (await apiClient.get(`/entity_my/members/${entityCd}`)) as any;
    }
    const res: any = await apiClient.get(`/documents/variables/shares/${entityCd}`);
    const shares = res?.data?.shares || res?.data || [];
    return {
      success: !!res?.success,
      data: { members: shares },
    };
  },

  /**
   * Contact 목록
   */
  getContacts: (entityCd: string): Promise<{ success: boolean; data: { contacts: any[] } }> => {
    return apiClient.get(`/documents/variables/contacts/${entityCd}`) as any;
  },

  /**
   * Entity 소속 L_OFFICER 전체 (CESSATION_DATE 없는 row, 직책 필터 없음)
   */
  getEntityOfficersAll: (
    entityCd: string,
  ): Promise<{
    success: boolean;
    data: {
      officers: Array<{
        contactCd: string;
        contactName: string;
        positions?: Array<{ positionCd?: string; positionName?: string; appointmentDate?: string }>;
      }>;
    };
  }> => {
    return apiClient.get(`/startTask/common/officers-by-entity-all/${entityCd}`) as any;
  },

  /**
   * Officer 상세 조회 — MY 는 entity_my/officer 의 새 주소체계 endpoint 사용,
   * SG 는 기존 documents/variables 사용.
   */
  getOfficerDetail: (entityCd: string, officerIdx: number): Promise<{ success: boolean; data: OfficerDetail }> => {
    const sessionStore = useSessionStore();
    const jurisdiction = String(sessionStore.getCustomerInfo()?.jurisdiction || '').toUpperCase();
    const isMY = jurisdiction === 'MY' || jurisdiction === 'MALAYSIA';
    const url = isMY
      ? `/entity_my/officer/${entityCd}/detail/${officerIdx}`
      : `/documents/variables/directors/${entityCd}/detail/${officerIdx}`;
    return apiClient.get(url) as any;
  },

  /**
   * Controller 상세 조회 — SG 는 L_CONTROLLERMANAGE 를 ctrlIdx 로 조회(REFERENCES_CONTACT_CD 가
   * 비어 contactCd 로는 못 잡으므로 ctrlIdx + registerType 가 키). MY 도 동일 라우트 형태.
   */
  getControllerDetail: (
    entityCd: string,
    ctrlIdx: string | number,
    registerType?: string,
  ): Promise<{ success: boolean; data: any }> => {
    const sessionStore = useSessionStore();
    const jurisdiction = String(sessionStore.getCustomerInfo()?.jurisdiction || '').toUpperCase();
    const isMY = jurisdiction === 'MY' || jurisdiction === 'MALAYSIA';
    const base = isMY ? '/entity_my/controller' : '/entity/controller';
    const params: Record<string, string> = { ctrlIdx: String(ctrlIdx) };
    if (registerType) params.registerType = String(registerType);
    return apiClient.get(`${base}/${entityCd}/detail`, { params }) as any;
  },

  /**
   * Contact 상세 조회
   * @param addressScope 'officer' = entity+MAP 신주소만 / 기본 = contact 직속 ADDRESS/BRANCH 만
   * @param positionCd officer 스코프일 때 MAP POSITION_CD 필터 (예: B30046 BO, B30034 Member)
   */
  getContactDetail: (
    entityCd: string,
    contactCd: string,
    opts?: { addressScope?: 'officer' | 'person'; positionCd?: string },
  ): Promise<{ success: boolean; data: any }> => {
    const addressScope = opts?.addressScope || 'person';
    const params: Record<string, string> = { addressScope };
    if (opts?.positionCd) params.positionCd = opts.positionCd;
    return apiClient.get(`/documents/variables/contacts/${entityCd}/detail/${contactCd}`, {
      params,
    }) as any;
  },

  /**
   * 세션에 entityCd 저장 (플러그인용)
   */
  setEntityContext: (entityCd: string, source: string = 'docGen'): Promise<any> => {
    return apiClient.post('/documents/variables/context', { entityCd, source }) as any;
  },

  /**
   * Task 제목 업데이트
   */
  updateTaskTitle: (taskId: number, taskTitle: string): Promise<{ success: boolean }> => {
    return apiClient.patch('/aidocuments/update-task-title', { taskId, taskTitle }) as any;
  },

  /**
   * 문서 파일명 업데이트
   */
  updateDocFilename: (saveId: number, filename: string): Promise<{ success: boolean }> => {
    return apiClient.patch('/aidocuments/update-doc-filename', { saveId, filename }) as any;
  },

  /**
   * 문서 미리보기 이미지 생성 (템플릿 변수 치환 → OnlyOffice PNG 변환)
   */
  previewImage: (params: {
    templateId?: number;
    uploadId?: string;
    saveId?: number;
    fileName?: string;
    formData?: Record<string, any>;
  }): Promise<{ success: boolean; imageUrl?: string; imageType?: string; message?: string }> => {
    return apiClient.post('/aidocuments/preview-image', params) as any;
  },

  /**
   * AI Document 직접 저장 (OnlyOffice History API → DB)
   * callback 의존 없이 OnlyOffice에서 직접 다운로드하여 DB에 저장
   */
  forceSave: (key: string, saveId: number): Promise<{
    success: boolean;
    message?: string;
    fileSize?: number;
    signerUpdated?: boolean;
    noChanges?: boolean;
  }> => {
    return apiClient.post('/aidocuments/force-save', { key, saveId }) as any;
  },

  /**
   * 사이너 정보 업데이트 (플러그인 데이터 → SIGNER_JSON)
   * doc-cust-save PUT /:saveId/signers와 동일 패턴
   */
  updateSigners: (saveId: number, signers: any[]): Promise<{
    success: boolean;
    message?: string;
    signerCount?: number;
  }> => {
    return apiClient.put('/aidocuments/update-signers', { saveId, signers }) as any;
  },

  /**
   * T-Sign 요약 정보 조회 (taskId 기반)
   */
  getTSignSummary: (taskId: number | string): Promise<{
    success: boolean;
    data: {
      docGuid: string;
      docTitle: string;
      docStatus: string;
      requestedAt: string | null;
      updatedAt: string | null;
      participantCount: number;
      pdfCount: number;
      saveIds?: number[];
    } | null;
    items?: Array<{
      docGuid: string;
      docTitle: string;
      docStatus: string;
      requestedAt: string | null;
      updatedAt: string | null;
      participantCount: number;
      pdfCount: number;
      saveIds?: number[];
    }>;
  }> => {
    return apiClient.get(`/tsign/documents/task/${taskId}/summary`) as any;
  },

  /**
   * Variable Value History — 이전 생성 이력 조회
   */
  getVariableHistory: (params: { tmpId: number; entityCd?: string }): Promise<{
    success: boolean;
    history: Array<{
      saveId: number;
      taskId: number;
      taskTitle: string;
      entityCd: string;
      entityName: string;
      fileName: string;
      createdBy: string;
      dataJson: Record<string, any>;
      date: string;
    }>;
  }> => {
    return apiClient.get('/aidocuments/docgen/variable-history', { params }) as any;
  },
};
